import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import * as MessageActions from 'src/app/modules/ngrx-store/message/message.actions';
import * as YourHomeActions from 'src/app/modules/ngrx-store/your-home/your-home.actions';
import { getHomeDetails, getSalesAgreementId } from 'src/app/modules/ngrx-store/your-home/your-home.selectors';

import { MessageService } from 'src/app/modules/core/services/message.service';

import { displayError } from 'src/app/modules/shared/utilities/notifications.utils';

@Injectable()
export class MessageEffects {
	constructor(
		private actions$: Actions,
		private messageService: MessageService,
		private store: Store
	) { }

	loadMessageThreads$ = createEffect(() => this.actions$.pipe(
		ofType(YourHomeActions.loadHomeDetailsSuccess, MessageActions.loadMessageThreads),
		withLatestFrom(this.store.select(getSalesAgreementId)),
		map(([, salesAgreementId]) => salesAgreementId),
		filter(Boolean), // Checks to make sure salesAgreementId is not undefined
		switchMap(salesAgreementId =>
			this.messageService.getMessageThreads(salesAgreementId).pipe(
				map((messageThreads) => MessageActions.loadMessageThreadsSuccess({ messageThreads })),
				catchError((err) => of(MessageActions.loadMessageThreadsFailure({ error: err })))
			))
	));

	loadMessageThreadsFailure$ = createEffect(() => this.actions$.pipe(
		ofType(MessageActions.loadMessageThreadsFailure),
		tap(err => {
			displayError(err.error);
		})
	),
	{ dispatch: false }
	);

	loadMessageItems$ = createEffect(() => this.actions$.pipe(
		ofType(MessageActions.loadMessageItems),
		withLatestFrom(this.store.select(getSalesAgreementId)),
		filter(([, salesAgreementId]) => Boolean(salesAgreementId)), // Checks to make sure salesAgreementId is not undefined
		switchMap(([action, salesAgreementId]) =>
			this.messageService.getMessageItems(salesAgreementId as number, action.messageThreadId).pipe(
				map((messageItems) => MessageActions.loadMessageItemsSuccess({ messageItems })),
				catchError((err) => of(MessageActions.loadMessageItemsFailure({ error: err })))
			))
	));

	loadMessageItemsFailure$ = createEffect(() => this.actions$.pipe(
		ofType(MessageActions.loadMessageItemsFailure),
		tap(err => {
			displayError(err.error);
		})
	),
	{ dispatch: false }
	);
	
	postMessageThread$ = createEffect(() => this.actions$.pipe(
		ofType(MessageActions.postMessageThread),
		withLatestFrom(
			this.store.select(getSalesAgreementId),
			this.store.select(getHomeDetails) // Selector to get homeDetails from the store
		),
		filter(([, salesAgreementId, homeDetails]) => Boolean(salesAgreementId) && Boolean(homeDetails)), // Ensure both salesAgreementId and homeDetails are defined
		switchMap(([action, salesAgreementId, homeDetails]) => {
			const assigneeIds = homeDetails?.lot?.fieldManagerLotAssocs?.map(fm => fm.contactId) || [];

			// Call the service with the combined assigneeIds
			return this.messageService.postMessageThread(
				salesAgreementId as number,
				action.messageThread,
				assigneeIds
			).pipe(
				map((messageThread) => MessageActions.postMessageThreadSuccess({ messageThread })),
				catchError((err) => of(
					MessageActions.postMessageThreadFailure({
						error: err
					})
				))
			);
		})
	));

	postMessageThreadFailure$ = createEffect(() => this.actions$.pipe(
		ofType(MessageActions.postMessageThreadFailure),
		tap(err => {
			displayError(err.error);
		})
	),
	{ dispatch: false }
	);

	postMessageItem$ = createEffect(() => this.actions$.pipe(
		ofType(MessageActions.postMessageItem),
		withLatestFrom(
			this.store.select(getSalesAgreementId),
			this.store.select(getHomeDetails) // Selector to get homeDetails from the store
		),
		filter(([, salesAgreementId, homeDetails]) => Boolean(salesAgreementId) && Boolean(homeDetails)),
		switchMap(([action, salesAgreementId, homeDetails]) => {
			const assigneeIds = homeDetails?.lot?.fieldManagerLotAssocs?.map(fm => fm.contactId) || [];

			return this.messageService.postMessageItem(salesAgreementId as number, action.messageItem.messageThreadId, action.messageItem, assigneeIds).pipe(
				map((messageItem) => MessageActions.postMessageItemSuccess({ messageItem })),
				catchError((err) => of(
					MessageActions.postMessageItemFailure({
						error: err
					})
				))
			)
		})
	));

	postMessageItemFailure$ = createEffect(() => this.actions$.pipe(
		ofType(MessageActions.postMessageItemFailure),
		tap(err => {
			displayError(err.error);
		})
	),
	{ dispatch: false }
	);

	markAsRead$ = createEffect(() => this.actions$.pipe(
		ofType(MessageActions.markAsRead),
		withLatestFrom(
			this.store.select(getSalesAgreementId), // Selector to get salesAgreementId from the store,
		),
		filter(([, salesAgreementId]) => Boolean(salesAgreementId)), // Checks to make sure salesAgreementId is not undefined
		switchMap(([action, salesAgreementId]) =>
			this.messageService.markAsRead(salesAgreementId as number, action.messageThreadId).pipe(
				map((messageThread) => MessageActions.markAsReadSuccess({ messageThread })),
				catchError((err) => of(
					MessageActions.markAsReadFailure({
						error: err
					})
				))
			)
		)
	));
}